import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, output, signal } from '@angular/core';
import { HdBodygraphSide, HdPlanetGridVisualizationMode } from '../../hd-bodygraph.models';
import { HdChartHoverHighlightService } from '../../hd-chart-hover-highlight.service';
import { PhsCategory } from '../../hd-phs-metadata';
import { HdColorShapeComponent } from '../hd-color-shape/hd-color-shape.component';
import { HdPlanetGridDensity } from '../hd-planet-activation-block/hd-planet-activation-block.component';
import { HdPlanetGridColorConfig } from '../hd-planet-grid-color-config';
import { HdToneScaleComponent } from '../hd-tone-scale/hd-tone-scale.component';
import { HdToneShapeComponent } from '../hd-tone-shape/hd-tone-shape.component';

/**
 * Klik-event op een PHS color/tone-blok in de planet-grid, verrijkt door de
 * cross-componenten (incarnation/life) met de PHS-categorie + kleur/toon zodat
 * de chart-pagina het juiste info-panel kan openen via smart-placement.
 * Origin = cursor (muis) of midden van het blok (toetsenbord). De `colorConfig`
 * volgt mee zodat het info-panel dezelfde shape-rendering (classic vs modern)
 * kan tonen als de chart-block waarop geklikt is.
 */
export interface HdPhsBlockClickEvent {
  readonly category: PhsCategory;
  readonly side: HdBodygraphSide;
  readonly color: number;
  readonly tone: number;
  readonly colorConfig: HdPlanetGridColorConfig;
  readonly origin: { readonly x: number; readonly y: number };
}

/**
 * Displays the Color/Tone block for the personality side (Awareness/Motivation, Perspective/View)
 * or design side (Digestion/Determination, Environment).
 *
 * Layout:
 * - Color shape: a circle split horizontally (top half + bottom half).
 * - Tone shape: a triangle split vertically (left half + right half).
 *
 * Color rules (vertical split):
 * - 1-3 → top = primary (blue), bottom = side color
 * - 4-6 → top = side color, bottom = primary (blue)
 *
 * Tone rules (horizontal split):
 * - 1-3 → right = primary (blue), left = side color
 * - 4-6 → right = side color, left = primary (blue)
 *
 * Side color is `colorToneShape.personality` for personality side and `colorToneShape.design` for design side.
 */
@Component({
  selector: 'hd-color-tone-block',
  templateUrl: './hd-color-tone-block.component.html',
  styleUrl: './hd-color-tone-block.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdColorShapeComponent, HdToneScaleComponent, HdToneShapeComponent, NgTemplateOutlet],
  host: {
    'role': 'button',
    'tabindex': '0',
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
    '(click)': 'emitBlockClicked($event)',
    '(keydown.enter)': 'emitBlockClicked($event)',
    '(keydown.space)': 'emitBlockClicked($event)',
    '[class.is-hover-match]': 'isHoverMatch()',
    '[class.is-hover-source]': 'isHoverSource()',
    '[class.is-hover-dimmed]': 'isHoverDimmed()',
    '[class.is-dim-disabled]': 'isDimDisabled()',
  },
})
export class HdColorToneBlockComponent {
  private readonly hostElementRef = inject(ElementRef<HTMLElement>);

  /**
   * Klik-event op het volledige kleur/toon-blok. Bedoeld voor de planet-grid
   * (Sun/Earth-motivatie & nodes-view) om een PHS-info-dialog te openen
   * — analoog aan {@link HdPlanetActivationBlockClickEvent} maar zonder
   * activatie-referentie (de parent kent kleur+toon+categorie via zijn
   * eigen inputs en kan het event verrijken). `origin` is de cursor-positie
   * (muisklik) of het midden van het blok (toetsenbord).
   */
  readonly phsBlockClicked = output<{ origin: { x: number; y: number } }>();
  readonly colorNumber = input.required<number>();
  readonly toneNumber = input.required<number>();
  readonly side = input.required<HdBodygraphSide>();
  readonly colorConfig = input.required<HdPlanetGridColorConfig>();
  /**
   * Poorten waar dit kleur/toon-blok inhoudelijk bij hoort (motivatie =
   * Sun + Earth, view = NN + SN). Wordt door de parent (incarnation-cross /
   * life-cross) doorgegeven. Bij hover wordt dezelfde highlight-state op
   * de chart getriggerd als wanneer die poorten afzonderlijk gehoverd
   * zouden worden.
   */
  readonly relatedGates = input<readonly number[]>([]);
  /** Kleur-icoon (kale FA-naam) — enkel in extended-modus; `null` = classic (geen iconen). */
  readonly colorIcon = input<string | null>(null);
  /** Cognitie-icoon (kale FA-naam) op basis van de toon — enkel in extended-modus. */
  readonly cognitionIcon = input<string | null>(null);
  /** Hover-tooltip (keynote) voor het kleur-icoon. */
  readonly colorTooltip = input<string | null>(null);
  /** Hover-tooltip (keynote) voor het cognitie-icoon. */
  readonly cognitionTooltip = input<string | null>(null);
  /** Toon-variant-icoon (kale FA-naam) — de actieve links/rechts-variant van de kleur. */
  readonly variantIcon = input<string | null>(null);
  /** Hover-tooltip (keynote) voor het toon-variant-icoon. */
  readonly variantTooltip = input<string | null>(null);
  /** Weergavemodus: classic | extended | modern | keynotes. */
  readonly mode = input<HdPlanetGridVisualizationMode>('classic');
  /**
   * Mini-densiteit (mobiel): compact blok naast de mini planet-blokken. De
   * planet-grid forceert in mini de classic-modus, dus enkel de twee vormen
   * met cijfer worden verkleind getoond. Zie {@link HdPlanetGridDensity}.
   */
  readonly density = input<HdPlanetGridDensity>('normal');
  /** Naam van de actieve toon-variant (bv. "Kalm") — voor de keynotes-modus. */
  readonly variantName = input<string | null>(null);
  /** Cognitie-naam (bv. "Smaak") — voor de keynotes-modus. */
  readonly cognitionName = input<string | null>(null);

  private readonly hoverService = inject(HdChartHoverHighlightService, { optional: true });
  private readonly hostHovered = signal(false);

  protected readonly isMirrored = computed(() => this.side() === 'design');

  protected readonly isHoverMatch = computed(() => {
    const service = this.hoverService;
    if (!service?.enabled() || !service?.isActive()) return false;
    const gates = this.relatedGates();
    return gates.some(gate => service.isGateMatchingHover(gate));
  });

  protected readonly isHoverSource = computed(() => {
    const service = this.hoverService;
    if (!service?.enabled() || !service?.isActive()) return false;
    return this.hostHovered() && service.state()?.source === 'planet-grid-furnishing';
  });

  protected readonly isHoverDimmed = computed(() => {
    const service = this.hoverService;
    if (!service?.enabled() || !service?.isActive()) return false;
    if (!service.dimsGrid()) return false;
    return !this.isHoverMatch();
  });

  /** "Dimmen bij hover" uit: matches tonen dan de blauwe rand (zie SCSS). */
  protected readonly isDimDisabled = computed(() =>
    this.hoverService != null && !this.hoverService.dimOnHover(),
  );

  protected onMouseEnter(): void {
    this.hostHovered.set(true);
    this.hoverService?.setGatesHover(this.relatedGates(), 'planet-grid-furnishing');
  }

  protected onMouseLeave(): void {
    this.hostHovered.set(false);
    this.hoverService?.setGatesHover(null, 'planet-grid-furnishing');
  }

  /**
   * Host-listener voor LMB en Enter/Space. Emit het cursor- of midden-blok-
   * punt zodat het floating-panel via smart-placement nabij het blok opengaat.
   */
  protected emitBlockClicked(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.phsBlockClicked.emit({ origin: this.resolveClickOrigin(event) });
  }

  private resolveClickOrigin(event: Event): { x: number; y: number } {
    if (event instanceof MouseEvent && (event.clientX !== 0 || event.clientY !== 0)) {
      return { x: event.clientX, y: event.clientY };
    }
    const rect = this.hostElementRef.nativeElement.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }

  /**
   * Side-specific accent (gebruikt voor de FA-iconen rond de shapes in extended/
   * modern). De shapes-zelf bepalen hun fills via {@link HdColorShapeComponent}
   * / {@link HdToneShapeComponent} op basis van dezelfde colorConfig.
   */
  protected readonly sideColor = computed(() => {
    const shape = this.colorConfig().colorToneShape;
    return this.side() === 'design' ? shape.design : shape.personality;
  });

  protected readonly isClassicStyle = computed(() => this.colorConfig().beneathTheLineStyle === 'classic');
}
